import React, {ChangeEvent} from 'react'
import {Box, Stack, styled, IconButton, TextField} from '@mui/material'
import {AssistantTimelineViewConfig} from './AssistantTimelineViewConfig'
import {
  ChatAdapter,
  TimelineAction,
  TimelineActionManager,
  TimelineEventProducer
} from '@fnd/timeline'
import AttachImageButton from './buttons/AttachImageButton'
import CancelIcon from '@mui/icons-material/Cancel'
import LocationPickerButton from './buttons/LocationPickerButton'
import DateTimePickerButton from './buttons/DateTimePickerButton'
import kc from 'web-common/services/auth'
import {v4 as UUIDv4} from 'uuid'
import FileStorage from 'web-common/services/files/FileStorage'
import {DateTimeEntry} from './actions/DateTimePicker'
import {withTranslation, WithTranslation} from 'react-i18next'
import SendMessageButton from 'web-common/views/timeline/components/SendMessageButton'
import VoiceRecordButton from 'web-common/views/timeline/components/VoiceRecordButton'

const InputWrapper = styled(Box)(({theme}) => ({
  padding: '1rem',
  display: 'flex',
  flexGrow: 0,
  border: 0
}))

const ActionsWrapper = styled(Box)(({theme}) => ({
  padding: '0.5rem 0',
  background: theme.palette.common.white
}))

const MessageFieldWrapper = styled(Stack)(({theme}) => ({
  width: '100%',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: 30,
  paddingLeft: 10,
  paddingRight: 10,
  paddingBottom: 10,

  [theme.breakpoints.up('md')]: {
    width: '80%',
    margin: '0 auto'
  }
}))

const AttachedImagePreview = styled('div')(({theme}) => ({
  position: 'relative',
  width: 40,
  height: 40,
  borderRadius: 10,
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}))

const DeleteAttachedImageButton = styled(IconButton)({
  position: 'absolute',
  top: 2,
  right: 2,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  borderRadius: '50%',
  padding: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)'
  }
})

interface Attachment {
  blob: string
  path: string
}

interface AssistantTimelineMessageCreatorProps extends WithTranslation {
  actionManager: TimelineActionManager
  producer: TimelineEventProducer
  config: AssistantTimelineViewConfig
  initMessage?: string
}

interface AssistantTimelineMessageCreatorState {
  message: string
  sending: boolean
  disabled: boolean
  actions: TimelineAction<any>[]
  attachments: Attachment[]
}

const MAX_ATTACHMENTS = 1

class AssistantTimelineMessageCreator extends React.Component<
  AssistantTimelineMessageCreatorProps,
  AssistantTimelineMessageCreatorState
> {
  state: AssistantTimelineMessageCreatorState = {
    message: '',
    disabled: false,
    sending: false,
    actions: [],
    attachments: []
  }

  ref = React.createRef<HTMLInputElement>()
  inputSending = false

  componentDidMount() {
    this.props.actionManager.subscribe((actions) => {
      this.setState({actions})
    })

    this.props.actionManager.settings((settings) => {
      this.setState({disabled: settings.disabled})
    })

    if (this.props.initMessage) {
      this.setState({message: this.props.initMessage})
    }
  }

  componentDidUpdate(
    prevProps: AssistantTimelineMessageCreatorProps,
    prevState: AssistantTimelineMessageCreatorState
  ) {
    if (prevState.disabled && !this.state.disabled) {
      this.ref.current?.focus()
    }
  }

  onSendMessage() {
    if (this.inputSending) return

    const hasText = this.state.message.trim() !== ''
    const hasAttachments = this.state.attachments.length > 0
    if (!hasText && !hasAttachments) return

    this.inputSending = true
    this.setState({sending: true}, async () => {
      const attachment = this.state.attachments[0]

      await this.props.producer.createEvent({
        message: this.state.message.trim(),
        adapterId: ChatAdapter.id,
        ...(attachment
          ? {
              data: {
                metadata: {
                  action: 'SHARED_ATTACHMENT',
                  attachment: attachment.path
                }
              }
            }
          : {})
      })

      this.setState(
        {
          message: '',
          sending: false,
          attachments: []
        },
        () => {
          this.ref.current?.focus()
          this.inputSending = false
        }
      )
    })
  }

  onUpdateMessage(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    this.setState({message: e.target.value})
  }

  onMessageEnter(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      this.onSendMessage()
      e.preventDefault()
    }
  }

  onPasteMessage = (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items

    if (this.state.attachments.length >= MAX_ATTACHMENTS) {
      e.preventDefault()
      return
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i]

      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile()

        if (file) {
          const reader = new FileReader()

          reader.onload = async (event) => {
            const blob = event.target?.result as string

            const userId = kc.instance.subject!
            const uploadPath = `user/${userId}/assistant/`
            const fileName = `${UUIDv4()}.${file.name.split('.').pop()}`

            try {
              const filePath = await FileStorage.uploadResource(
                file,
                uploadPath,
                fileName,
                true
              )

              this.setState((prev) => {
                if (prev.attachments.length < MAX_ATTACHMENTS) {
                  return {
                    attachments: [...prev.attachments, {blob, path: filePath}]
                  }
                }
                return null
              })
            } catch (err) {
              console.error('Failed to upload pasted image', err)
            }
          }

          reader.readAsDataURL(file)
          e.preventDefault()
          break
        }
      }
    }
  }

  handleImageSelect = (blob: string, path: string) => {
    this.setState((prev) => {
      if (prev.attachments.length < MAX_ATTACHMENTS) {
        return {attachments: [...prev.attachments, {blob, path}]}
      }
      return null
    })
  }

  handleDeleteAttachedImage = async (index: number) => {
    try {
      const attachment = this.state.attachments[index]
      await FileStorage.deleteResource(attachment.path, true)

      this.setState((prev) => ({
        attachments: prev.attachments.filter((_, i) => i !== index)
      }))
    } catch (error) {
      console.error('Failed to delete file from storage:', error)
    }
  }

  handleAudioTranscribe = async (message: string) => {
    this.setState((prevState) => ({
      message: prevState.message ? `${prevState.message} ${message}` : message
    }))
  }

  handleLocationPicked = async (
    location: {lat: number; lon: number},
    address: string,
    regions?: string[],
    range?: number,
    variant?: string
  ) => {
    const regionsList =
      variant === 'location'
        ? regions && regions.length > 0
          ? [regions[0]]
          : []
        : []

    const officesList = variant === 'range' ? regions || [] : []
    const message = range
      ? address +
        `\n${this.props.t('fnd-assistant-dialog-location-type-2')}: ${range / 1000}km`
      : address

    await this.props.producer.createEvent({
      message,
      adapterId: ChatAdapter.id,
      data: {
        metadata: {
          action: 'SHARED_LOCATION',
          location: {
            regions: regionsList,
            offices: officesList,
            address: address,
            lat: location.lat,
            lon: location.lon,
            range: range
          }
        }
      }
    })
  }

  handleSelectDates = async (dates: DateTimeEntry[], message: string) => {
    if (dates.length > 0) {
      const firstEntry = dates[0]

      if (firstEntry.date) {
        const timestamp = firstEntry.date.getTime()

        await this.props.producer.createEvent({
          message,
          adapterId: ChatAdapter.id,
          data: {
            metadata: {
              action: 'SHARED_DATE',
              eventDate: timestamp
            }
          }
        })
      }
    }
  }

  renderActionController() {
    if (this.state.actions.length > 0) {
      return (
        <ActionsWrapper>
          <Stack direction="row" justifyContent="center" columnGap={1}>
            {this.state.actions.map((a) => {
              const El = a.node
              return (
                /* @ts-ignore */
                <El {...a.props} producer={this.props.producer} key={a.id} />
              )
            })}
          </Stack>
        </ActionsWrapper>
      )
    }
    return null
  }

  renderMessageController() {
    return (
      <InputWrapper>
        <MessageFieldWrapper>
          {/* User message input */}
          <TextField
            fullWidth
            multiline
            maxRows={2}
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                }
              }
            }}
            value={this.state.message}
            placeholder={
              this.state.disabled
                ? this.props.t(
                    'fnd-assistant-message-creator-placeholder-disabled'
                  )
                : this.props.t(
                    'fnd-assistant-message-creator-placeholder-enabled'
                  )
            }
            autoFocus={this.props.config.accessibility.messageCreator.autoFocus}
            inputRef={this.ref}
            disabled={this.state.sending || this.state.disabled}
            onChange={this.onUpdateMessage.bind(this)}
            onKeyDown={this.onMessageEnter.bind(this)}
            onPaste={this.onPasteMessage.bind(this)}
          />

          {/* Message input buttons */}
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Stack direction="row">
              {process.env.NODE_ENV === 'development' && (
                <>
                  <DateTimePickerButton
                    onSelectDates={this.handleSelectDates}
                  />
                  <LocationPickerButton
                    onLocationPicked={this.handleLocationPicked}
                  />
                </>
              )}

              <AttachImageButton
                onImageSelect={this.handleImageSelect}
                disabled={this.state.attachments.length >= MAX_ATTACHMENTS}
              />
              {this.state.attachments.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1} mx={1}>
                  {this.state.attachments.map((attachment, id) => (
                    <AttachedImagePreview key={id}>
                      <img src={attachment.blob} alt="attachment preview" />
                      <DeleteAttachedImageButton
                        onClick={() => this.handleDeleteAttachedImage(id)}
                      >
                        <CancelIcon sx={{fontSize: 16}} />
                      </DeleteAttachedImageButton>
                    </AttachedImagePreview>
                  ))}
                </Stack>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              <VoiceRecordButton onAudioRecorded={this.handleAudioTranscribe} />
              <SendMessageButton
                sending={this.state.sending}
                disabled={this.state.disabled}
                onClick={this.onSendMessage.bind(this)}
              />
            </Stack>
          </Stack>
        </MessageFieldWrapper>
      </InputWrapper>
    )
  }

  render() {
    return (
      <>
        {this.renderActionController()}
        {this.renderMessageController()}
      </>
    )
  }
}

export default withTranslation()(AssistantTimelineMessageCreator)
